import { useEffect, useMemo, useRef, useState } from "react";
import Fuse from "fuse.js";
import { useCourseGlossaryStore } from "@/stores/courseGlossaryStore";
import { useCourseDetailStore } from "@/stores/courseDetailStore";
import { SearchField } from "@/components/ui/search-field";
import { Switch } from "@/components/ui/switch";
import {
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Loader2 } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { splitAliases, getChapterTermIds } from "@/lib/glossaryFilter";
import type { GlossaryTerm } from "@/types/glossary";

interface GlossarySlideoverProps {
  courseId: string;
}

interface SearchableTerm {
  term: GlossaryTerm;
  name: string;
  aliases: string[];
}

const TermEntry = ({
  term,
  inChapter,
  focused,
  entryRef,
}: {
  term: GlossaryTerm;
  inChapter: boolean;
  focused: boolean;
  entryRef?: (node: HTMLLIElement | null) => void;
}) => {
  const aliasList = splitAliases(term.aliases);
  return (
    <li
      ref={entryRef}
      className={
        focused
          ? "-mx-2 rounded-md bg-amber-100/60 px-2 py-4 ring-2 ring-amber-300 transition-colors"
          : "py-4"
      }
    >
      <h3 className="text-base font-semibold text-primary">
        {inChapter ? (
          <mark
            title="Kommt im aktuellen Kapitel vor"
            className="rounded-sm bg-secondary box-decoration-clone text-white px-1.5 py-0.5"
          >
            {term.name}
          </mark>
        ) : (
          term.name
        )}
      </h3>
      {aliasList.length > 0 && (
        <p className="mt-0.5 text-sm text-muted-foreground">
          Auch: {aliasList.join(", ")}
        </p>
      )}
      {term.description && (
        <div className="prose prose-sm mt-1.5 max-w-none text-muted-foreground">
          <ReactMarkdown>{term.description}</ReactMarkdown>
        </div>
      )}
    </li>
  );
};

export const GlossarySlideover = ({ courseId }: GlossarySlideoverProps) => {
  const { terms, loading, error, fetchGlossary } = useCourseGlossaryStore();
  const focusTermId = useCourseGlossaryStore((state) => state.focusTermId);
  const storyboards = useCourseDetailStore((state) => state.storyboards);
  const storyboardsLoading = useCourseDetailStore(
    (state) => state.storyboardsLoading
  );
  const [search, setSearch] = useState("");
  const [chapterOnly, setChapterOnly] = useState(false);
  const focusRef = useRef<HTMLLIElement | null>(null);

  useEffect(() => {
    fetchGlossary(courseId);
  }, [courseId, fetchGlossary]);

  // When opened via an inline term ("Mehr im Glossar"), scroll that term into
  // view. The slideover mounts fresh with empty filters, so the term is listed.
  // Delayed past the sheet's open animation and Radix's auto-focus (which
  // scrolls the search input — i.e. the list top — back into view).
  useEffect(() => {
    if (!focusTermId) return;
    const timer = window.setTimeout(() => {
      focusRef.current?.scrollIntoView({ block: "center" });
    }, 350);
    return () => window.clearTimeout(timer);
  }, [focusTermId, terms]);

  const searchable = useMemo<SearchableTerm[]>(
    () =>
      terms.map((term) => ({
        term,
        name: term.name,
        aliases: splitAliases(term.aliases),
      })),
    [terms]
  );

  const fuse = useMemo(
    () =>
      new Fuse(searchable, {
        keys: [
          { name: "name", weight: 2 },
          { name: "aliases", weight: 1 },
        ],
        threshold: 0.35,
        ignoreLocation: true,
        minMatchCharLength: 2,
      }),
    [searchable]
  );

  const sortedTerms = useMemo(
    () =>
      [...terms].sort((a, b) =>
        a.name.localeCompare(b.name, "de", { sensitivity: "base" })
      ),
    [terms]
  );

  const chapterTermIds = useMemo(
    () => getChapterTermIds(terms, storyboards),
    [terms, storyboards]
  );

  const isSearching = search.trim().length > 0;
  // Search results keep fuse's relevance order; browsing is alphabetical.
  const baseResults = isSearching
    ? fuse.search(search).map((result) => result.item.term)
    : sortedTerms;
  const filteredTerms = chapterOnly
    ? baseResults.filter((term) => chapterTermIds.has(term.id))
    : baseResults;

  const chapterFilterAvailable = !storyboardsLoading && storyboards.length > 0;
  const isFiltered = isSearching || chapterOnly;
  const countLabel = isFiltered
    ? `${filteredTerms.length} von ${terms.length}`
    : `${terms.length} Begriffe`;

  return (
    <SheetContent
      side="right"
      className="  bg-bg-light w-full sm:max-w-2xl flex flex-col"
    >
      <SheetHeader className="border-b pb-4">
        <div className="flex items-baseline justify-between pr-8">
          <SheetTitle>Glossar</SheetTitle>
          {!loading && !error && (
            <p className="text-xs tabular-nums text-muted-foreground">
              {countLabel}
            </p>
          )}
        </div>
        <SheetDescription className="sr-only">
          Glossar mit Fachbegriffen zum Kurs
        </SheetDescription>
        <SearchField value={search} onChange={setSearch} />
        <div className="mt-1 flex items-center gap-2">
          <Switch
            id="glossary-chapter-filter"
            checked={chapterOnly}
            onCheckedChange={setChapterOnly}
            disabled={!chapterFilterAvailable}
          />
          <label
            htmlFor="glossary-chapter-filter"
            className="text-sm text-muted-foreground select-none"
          >
            Nur Begriffe aus dem aktuellen Kapitel
          </label>
        </div>
      </SheetHeader>

      <div className="flex-1 overflow-y-auto px-6 pb-6">
        {loading && (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="size-6 animate-spin text-muted-foreground" />
          </div>
        )}

        {error && (
          <p className="text-sm text-destructive py-4">
            Fehler beim Laden des Glossars: {error}
          </p>
        )}

        {!loading && !error && filteredTerms.length === 0 && (
          <div className="py-4">
            <p className="text-sm text-muted-foreground">
              {chapterOnly && !isSearching
                ? "Keine Begriffe für dieses Kapitel gefunden."
                : "Keine Begriffe gefunden."}
            </p>
            {chapterOnly && isSearching && (
              <p className="text-sm text-muted-foreground mt-1">
                Tipp: Deaktiviere den Kapitel-Filter, um alle Begriffe zu
                durchsuchen.
              </p>
            )}
          </div>
        )}

        {!loading && !error && filteredTerms.length > 0 && (
          <ul className="divide-y">
            {filteredTerms.map((term) => (
              <TermEntry
                key={term.id}
                term={term}
                inChapter={chapterTermIds.has(term.id)}
                focused={term.id === focusTermId}
                entryRef={
                  term.id === focusTermId
                    ? (node) => {
                        focusRef.current = node;
                      }
                    : undefined
                }
              />
            ))}
          </ul>
        )}
      </div>
    </SheetContent>
  );
};
